import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BlockConfirmModal.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

interface BlockConfirmModalProps {
    isOpen: boolean;
    targetName: string;
    onClose: () => void;
    /** confirm 시 사유 + 신고 체크 같이 전달. 결과는 부모에서 처리. */
    onConfirm: (payload: { reason?: string; report: boolean }) => Promise<void> | void;
}

/**
 * 사용자 차단 확정 모달.
 * - 차단 효과 안내 (메시지 가림 / 초대 제외 / 기존 모임 유지)
 * - 사유 입력 (선택)
 * - "신고도 함께 접수" 체크 — 현재는 플래그만 부모에 전달
 */
export const BlockConfirmModal: React.FC<BlockConfirmModalProps> = ({
    isOpen, targetName, onClose, onConfirm,
}) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [report, setReport] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleConfirm = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await onConfirm({ reason: reason.trim() || undefined, report });
            setReason('');
            setReport(false);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={cls("block-confirm-overlay")} onClick={onClose} role="presentation">
            <div className={cls("block-confirm-modal")} onClick={(e) => e.stopPropagation()}>
                <h3 className={cls("block-confirm-title")}>
                    {t('member.blockConfirmTitle', { name: targetName })}
                </h3>
                <ul className={cls("block-confirm-effects")}>
                    <li>{t('member.effect1')}</li>
                    <li>{t('member.effect2')}</li>
                    <li>{t('member.effect3')}</li>
                </ul>

                <label className={cls("block-confirm-field")}>
                    <span className={cls("block-confirm-field-label")}>{t('member.reasonLabel')}</span>
                    <input
                        type="text"
                        className={cls("block-confirm-input")}
                        placeholder={t('member.reasonPlaceholder')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        maxLength={200}
                    />
                </label>

                <label className={cls("block-confirm-report")}>
                    <input
                        type="checkbox"
                        checked={report}
                        onChange={(e) => setReport(e.target.checked)}
                    />
                    <span>{t('member.alsoReport')}</span>
                </label>

                <div className={cls("block-confirm-actions")}>
                    <button
                        type="button"
                        className={cls("block-confirm-cancel")}
                        onClick={onClose}
                        disabled={submitting}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        className={cls("block-confirm-submit")}
                        onClick={handleConfirm}
                        disabled={submitting}
                    >
                        {submitting ? t('member.processing') : t('member.block')}
                    </button>
                </div>
            </div>
        </div>
    );
};
